import { useCallback, useEffect, useRef, useState } from "react"

import { openSnackbar as openGlobalSnackbar } from "../../../../reducers/snackbar"
import {
  normalizeEmailUnreadRow,
  normalizeSmsUnreadRow,
  normalizeVoicemailUnreadRow,
  sortInboxNotifications,
  sumInboxCounts,
} from "./inboxNotificationModel"
import {
  INBOX_CHANNELS,
  getInboxMessagesPath,
  getInboxReadUrl,
} from "./inboxNotificationTypes"

export const EMAIL_INBOX_POLL_MS = 30000
export const SMS_INBOX_POLL_MS = 15000
export const VOICEMAIL_INBOX_POLL_MS = 30000

function inboxSeenStorageKey(profile) {
  // active_account is an object ({id, email, name}); interpolating it produced
  // the literal key "crm-inbox-seen:[object Object]" for every user and every
  // account, so a concierge shared one seen-state across all their accounts.
  return `crm-inbox-seen:${
    profile?.active_account?.id || profile?.id || "default"
  }`
}

function readInboxSeenState(profile) {
  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(inboxSeenStorageKey(profile)) || "null"
    )
    if (parsed && typeof parsed === "object" && parsed.seeded) {
      return parsed
    }
  } catch (_err) {
    return { seeded: false, threads: {} }
  }
  return { seeded: false, threads: {} }
}

function writeInboxSeenState(profile, threads) {
  window.sessionStorage.setItem(
    inboxSeenStorageKey(profile),
    JSON.stringify({ seeded: true, threads })
  )
}

function inboxSeenKeyForNotification(notification) {
  return `${notification.channel}:${notification.id}`
}

export function inboxSeenFromNotifications(lists) {
  const seen = {}
  for (const channel of Object.values(INBOX_CHANNELS)) {
    for (const notification of lists[channel] || []) {
      seen[inboxSeenKeyForNotification(notification)] =
        notification.unreadCount || 0
    }
  }
  return seen
}

function findNewlyArrivedNotifications(previousSeen, lists) {
  const newlyArrived = []
  for (const channel of Object.values(INBOX_CHANNELS)) {
    for (const notification of lists[channel] || []) {
      const unread = notification.unreadCount || 0
      const key = inboxSeenKeyForNotification(notification)
      const previous = previousSeen[key] || 0
      if (unread > previous) {
        newlyArrived.push(notification)
      }
    }
  }
  return newlyArrived
}

function latestInboxNotification(lists) {
  const combined = [
    ...(lists[INBOX_CHANNELS.email] || []),
    ...(lists[INBOX_CHANNELS.sms] || []),
    ...(lists[INBOX_CHANNELS.voicemail] || []),
  ]
  return sortInboxNotifications(combined)[0]
}

function inboxChannelFlagsFromNotifications(notifications) {
  const hasSms = notifications.some((row) => row.channel === INBOX_CHANNELS.sms)
  const hasEmail = notifications.some(
    (row) => row.channel === INBOX_CHANNELS.email
  )
  const hasVoicemail = notifications.some(
    (row) => row.channel === INBOX_CHANNELS.voicemail
  )
  return { hasSms, hasEmail, hasVoicemail }
}

function inboxAlertMessage(notifications) {
  if (notifications.length === 1) {
    const row = notifications[0]
    const kind =
      row.channel === INBOX_CHANNELS.sms
        ? "SMS"
        : row.channel === INBOX_CHANNELS.voicemail
        ? "voicemail"
        : "email"
    return `New ${kind} from ${row.title}: ${row.preview || ""}`
  }
  const { hasSms, hasEmail, hasVoicemail } =
    inboxChannelFlagsFromNotifications(notifications)
  const channels = [hasEmail, hasSms, hasVoicemail].filter(Boolean).length
  if (channels === 1) {
    if (hasSms) return `${notifications.length} new SMS messages`
    if (hasVoicemail) return `${notifications.length} new voicemails`
    return `${notifications.length} new emails`
  }
  return `${notifications.length} new inbox messages`
}

function desktopInboxTitle(notifications) {
  const { hasSms, hasEmail, hasVoicemail } =
    inboxChannelFlagsFromNotifications(notifications)
  if (hasSms && !hasEmail && !hasVoicemail) return "New SMS"
  if (hasEmail && !hasSms && !hasVoicemail) return "New email"
  if (hasVoicemail && !hasEmail && !hasSms) return "New voicemail"
  return "New message"
}

function desktopInboxTag(notifications) {
  const { hasSms, hasEmail, hasVoicemail } =
    inboxChannelFlagsFromNotifications(notifications)
  if (hasSms && !hasEmail && !hasVoicemail) return "crm-inbox-new-sms"
  if (hasEmail && !hasSms && !hasVoicemail) return "crm-inbox-new-mail"
  if (hasVoicemail && !hasEmail && !hasSms) return "crm-inbox-new-voicemail"
  return "crm-inbox-new-messages"
}

function showDesktopInboxNotification(notifications, message, onOpen) {
  if (
    typeof window === "undefined" ||
    typeof window.Notification === "undefined"
  ) {
    return
  }
  if (window.Notification.permission !== "granted") {
    return
  }
  const notification = new window.Notification(
    desktopInboxTitle(notifications),
    {
      body: message,
      tag: desktopInboxTag(notifications),
    }
  )
  notification.onclick = () => {
    window.focus()
    onOpen()
    notification.close()
  }
}

function countChannel(notifications) {
  return (notifications || []).reduce(
    (total, row) => total + (row.unreadCount || 0),
    0
  )
}

function buildCountsFromLists(lists) {
  const voicemail = countChannel(lists[INBOX_CHANNELS.voicemail])
  const email = countChannel(lists[INBOX_CHANNELS.email])
  const sms = countChannel(lists[INBOX_CHANNELS.sms])
  return {
    voicemail,
    email,
    sms,
    total: sumInboxCounts({ voicemail, email, sms }),
  }
}

const INITIAL_LISTS = {
  [INBOX_CHANNELS.voicemail]: [],
  [INBOX_CHANNELS.email]: [],
  [INBOX_CHANNELS.sms]: [],
}

const INITIAL_STATUS = {
  [INBOX_CHANNELS.voicemail]: "idle",
  [INBOX_CHANNELS.email]: "idle",
  [INBOX_CHANNELS.sms]: "idle",
}

export default function useInboxNotifications({
  axios,
  profile,
  profileLoaded,
  concierge,
  dispatch,
  navigate,
}) {
  const [lists, setLists] = useState(INITIAL_LISTS)
  const [counts, setCounts] = useState({
    voicemail: 0,
    email: 0,
    sms: 0,
    total: 0,
  })
  const [status, setStatus] = useState(INITIAL_STATUS)
  const seenInboxRef = useRef(null)
  const listsRef = useRef(lists)

  useEffect(() => {
    listsRef.current = lists
  }, [lists])

  const openInboxNotification = useCallback(
    (notification) => {
      if (!notification) return
      navigate(getInboxMessagesPath(notification))
    },
    [navigate]
  )

  const notifyNewInboxItems = useCallback(
    (notifications) => {
      const message = inboxAlertMessage(notifications)
      dispatch(openGlobalSnackbar({ message }))
      const latest = latestInboxNotification({
        [INBOX_CHANNELS.email]: notifications.filter(
          (row) => row.channel === INBOX_CHANNELS.email
        ),
        [INBOX_CHANNELS.sms]: notifications.filter(
          (row) => row.channel === INBOX_CHANNELS.sms
        ),
        [INBOX_CHANNELS.voicemail]: notifications.filter(
          (row) => row.channel === INBOX_CHANNELS.voicemail
        ),
      })
      const open = () => openInboxNotification(latest)
      if (
        typeof window.Notification !== "undefined" &&
        window.Notification.permission === "default"
      ) {
        window.Notification.requestPermission().then((permission) => {
          if (permission === "granted") {
            showDesktopInboxNotification(notifications, message, open)
          }
        })
        return
      }
      showDesktopInboxNotification(notifications, message, open)
    },
    [dispatch, openInboxNotification]
  )

  const loadEmailUnread = useCallback(() => {
    setStatus((current) => ({ ...current, [INBOX_CHANNELS.email]: "loading" }))
    return axios
      .get("/crm/api/zoho-mail/inbox/unread/")
      .then(({ data }) => {
        const emailList = sortInboxNotifications(
          (data.results || []).map(normalizeEmailUnreadRow)
        )
        setLists((current) => {
          const nextLists = { ...current, [INBOX_CHANNELS.email]: emailList }
          setCounts(buildCountsFromLists(nextLists))
          return nextLists
        })
        setStatus((current) => ({
          ...current,
          [INBOX_CHANNELS.email]: "loaded",
        }))
      })
      .catch(() => {
        setStatus((current) => ({
          ...current,
          [INBOX_CHANNELS.email]: "error",
        }))
      })
  }, [axios])

  const loadSmsUnread = useCallback(() => {
    setStatus((current) => ({ ...current, [INBOX_CHANNELS.sms]: "loading" }))
    return axios
      .get("/crm/api/concierge-sms/inbox/unread/")
      .then(({ data }) => {
        const smsList = sortInboxNotifications(
          (data.results || []).map(normalizeSmsUnreadRow)
        )
        setLists((current) => {
          const nextLists = { ...current, [INBOX_CHANNELS.sms]: smsList }
          setCounts(buildCountsFromLists(nextLists))
          return nextLists
        })
        setStatus((current) => ({ ...current, [INBOX_CHANNELS.sms]: "loaded" }))
      })
      .catch(() => {
        setStatus((current) => ({ ...current, [INBOX_CHANNELS.sms]: "error" }))
      })
  }, [axios])

  const loadVoicemailUnread = useCallback(() => {
    setStatus((current) => ({
      ...current,
      [INBOX_CHANNELS.voicemail]: "loading",
    }))
    return axios
      .get("/crm/api/inbound-voicemail/inbox/unread/")
      .then(({ data }) => {
        const voicemailList = sortInboxNotifications(
          (data.results || []).map(normalizeVoicemailUnreadRow)
        )
        setLists((current) => {
          const nextLists = {
            ...current,
            [INBOX_CHANNELS.voicemail]: voicemailList,
          }
          setCounts(buildCountsFromLists(nextLists))
          return nextLists
        })
        setStatus((current) => ({
          ...current,
          [INBOX_CHANNELS.voicemail]: "loaded",
        }))
      })
      .catch(() =>
        axios
          .get("/crm/api/inbound-voicemail/inbox/", {
            params: { page_size: 50 },
          })
          .then(({ data }) => {
            const unreadRows = (data.results || []).filter(
              (row) => row.is_read === false
            )
            const voicemailList = sortInboxNotifications(
              unreadRows.map(normalizeVoicemailUnreadRow)
            )
            setLists((current) => {
              const nextLists = {
                ...current,
                [INBOX_CHANNELS.voicemail]: voicemailList,
              }
              const nextCounts = buildCountsFromLists(nextLists)
              if (typeof data.unread_count === "number") {
                nextCounts.voicemail = data.unread_count
                nextCounts.total = sumInboxCounts(nextCounts)
              }
              setCounts(nextCounts)
              return nextLists
            })
            setStatus((current) => ({
              ...current,
              [INBOX_CHANNELS.voicemail]: "loaded",
            }))
          })
          .catch(() => {
            setStatus((current) => ({
              ...current,
              [INBOX_CHANNELS.voicemail]: "error",
            }))
          })
      )
  }, [axios])

  const refresh = useCallback(() => {
    if (!concierge || !profileLoaded) return
    loadEmailUnread()
    loadSmsUnread()
    loadVoicemailUnread()
  }, [
    concierge,
    loadEmailUnread,
    loadSmsUnread,
    loadVoicemailUnread,
    profileLoaded,
  ])

  const markSeenFromLists = useCallback(() => {
    const nextThreads = inboxSeenFromNotifications(listsRef.current)
    seenInboxRef.current = { seeded: true, threads: nextThreads }
    writeInboxSeenState(profile, nextThreads)
  }, [profile])

  const clearChannel = useCallback(
    async (channel) => {
      const items = listsRef.current[channel] || []
      if (!items.length) return

      const previousLists = listsRef.current
      const previousCounts = counts
      const clearedLists = { ...listsRef.current, [channel]: [] }
      const clearedCounts = buildCountsFromLists(clearedLists)

      setLists(clearedLists)
      setCounts(clearedCounts)

      try {
        // No per-request .catch here: swallowing each failure meant
        // Promise.all could never reject, the rollback below was unreachable,
        // and a wholly failed "Clear all" still looked like it worked.
        await Promise.all(
          items.map((notification) => {
            const url = getInboxReadUrl(notification)
            return url ? axios.post(url) : Promise.resolve()
          })
        )
        markSeenFromLists()
      } catch (_err) {
        setLists(previousLists)
        setCounts(previousCounts)
        refresh()
      }
    },
    [axios, counts, markSeenFromLists, refresh]
  )

  useEffect(() => {
    if (!concierge || !profileLoaded) {
      setLists(INITIAL_LISTS)
      setCounts({ voicemail: 0, email: 0, sms: 0, total: 0 })
      setStatus(INITIAL_STATUS)
      seenInboxRef.current = null
      return undefined
    }

    seenInboxRef.current = readInboxSeenState(profile)
    loadEmailUnread()
    loadSmsUnread()
    loadVoicemailUnread()

    const emailIntervalId = window.setInterval(
      loadEmailUnread,
      EMAIL_INBOX_POLL_MS
    )
    const smsIntervalId = window.setInterval(loadSmsUnread, SMS_INBOX_POLL_MS)
    const voicemailIntervalId = window.setInterval(
      loadVoicemailUnread,
      VOICEMAIL_INBOX_POLL_MS
    )

    return () => {
      window.clearInterval(emailIntervalId)
      window.clearInterval(smsIntervalId)
      window.clearInterval(voicemailIntervalId)
    }
  }, [
    concierge,
    loadEmailUnread,
    loadSmsUnread,
    loadVoicemailUnread,
    profile,
    profileLoaded,
    profile?.active_account,
  ])

  useEffect(() => {
    if (!concierge || !profileLoaded) return
    const allSettled = Object.values(INBOX_CHANNELS).every(
      (channel) => status[channel] === "loaded" || status[channel] === "error"
    )
    if (!allSettled) return

    const previousState = seenInboxRef.current || { seeded: false, threads: {} }
    const nextThreads = inboxSeenFromNotifications(lists)
    if (previousState.seeded) {
      const newlyArrived = findNewlyArrivedNotifications(
        previousState.threads || {},
        lists
      )
      if (newlyArrived.length) {
        notifyNewInboxItems(newlyArrived)
      }
    }
    seenInboxRef.current = { seeded: true, threads: nextThreads }
    writeInboxSeenState(profile, nextThreads)
  }, [concierge, lists, notifyNewInboxItems, profile, profileLoaded, status])

  return {
    counts,
    lists,
    status,
    refresh,
    clearChannel,
    markSeenFromLists,
  }
}
